import styled from 'styled-components'
import moment from 'moment'
import Gas from './Gas'
import Relayer from './Relayer'
import Bot from './Bot'
import Activity from './Activity'
import { formatDuration } from '../../../hooks/useCrons'
import Logs from './Logs'
import { ChainStatsResponse, CronStatusResponse } from '../../../types/API'
import { CRON_NAMES } from '../../../constants/networks'

export interface Props {
  chainId: number
  chainInfo: ChainStatsResponse
  crons?: CronStatusResponse[]
}

export default function Overview({ chainId, chainInfo, crons }: Props) {
  const cronName = CRON_NAMES[chainId] || ''
  const cronSelection = crons?.filter((cron) => cron.name.toLowerCase()?.includes(cronName))

  return (
    <Wrap>
      <MetricGrid>
        {chainId && <Bot chainId={chainId} chainInfo={chainInfo} />}
        {chainId && <Relayer chainId={chainId} chainInfo={chainInfo} />}
        {chainId && <Gas chainId={chainId} />}
        {chainId && <Activity chainId={chainId} chainInfo={chainInfo} />}
      </MetricGrid>

      {cronSelection && cronSelection.length > 0 && (
        <CronRow>
          <CronLabel>Cron</CronLabel>
          {cronSelection.map((item, i) => {
            const duration = moment.duration(moment(item.lastCompletionTime).diff(moment(item.lastStartTime)))
            const isStale = duration.asMinutes() < 2 && item.active !== true
            return (
              <CronItem key={i}>
                <CronDuration stale={isStale}>
                  {formatDuration(duration)}
                  {isStale && ' ⚠'}
                </CronDuration>
                <CronMeta>{moment(item.lastStartTime).format('DD/MM HH:mm')}</CronMeta>
                <CronStatus active={item.active === true}>
                  {item.active === true ? 'Running' : moment(item.lastCompletionTime).format('DD/MM HH:mm')}
                </CronStatus>
              </CronItem>
            )
          })}
        </CronRow>
      )}

      <SectionHeader>
        <SectionTitle>Recent Activity</SectionTitle>
      </SectionHeader>
      <Logs chainId={chainId} />

      {chainInfo?.transactions && (
        <TxSummary>
          <TxSummaryTitle>Last Hour Transactions</TxSummaryTitle>
          <TxStats>
            {[
              { label: 'Success', value: chainInfo.transactions.success, color: '#33C2B0' },
              { label: 'Missing', value: chainInfo.transactions.missing, color: '#FFCC33' },
              { label: 'Pending', value: chainInfo.transactions.pending, color: '#a89aff' },
              { label: 'Failed', value: chainInfo.transactions.failed, color: '#ff4444' },
            ].map(({ label, value, color }) => (
              <TxStat key={label}>
                <TxStatValue style={{ color }}>{value ?? '-'}</TxStatValue>
                <TxStatLabel>{label}</TxStatLabel>
              </TxStat>
            ))}
          </TxStats>
        </TxSummary>
      )}
    </Wrap>
  )
}

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  font-family: -apple-system, BlinkMacSystemFont, 'Inter', 'DMSans', sans-serif;
`

const MetricGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`

const CronRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 14px;
  background: #111;
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 6px;
  flex-wrap: wrap;
`

const CronLabel = styled.span`
  color: #777;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  font-weight: 600;
`

const CronItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const CronDuration = styled.span<{ stale: boolean }>`
  color: ${({ stale }) => stale ? '#FFCC33' : '#a0a0a0'};
  font-size: 12px;
  font-family: 'SF Mono', 'Fira Code', monospace;
`

const CronMeta = styled.span`
  color: #777;
  font-size: 11px;
  font-family: 'SF Mono', 'Fira Code', monospace;
`

const CronStatus = styled.span<{ active: boolean }>`
  color: ${({ active }) => active ? '#33C2B0' : '#555'};
  font-size: 11px;
`

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const SectionTitle = styled.h3`
  color: #777;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  margin: 0;
`

const TxSummary = styled.div`
  background: #111;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 8px;
  padding: 16px 20px;
`

const TxSummaryTitle = styled.div`
  color: #a1a1a1;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  font-weight: 600;
  margin-bottom: 12px;
`

const TxStats = styled.div`
  display: flex;
  gap: 32px;
  flex-wrap: wrap;

  @media (max-width: 400px) {
    gap: 20px;
  }
`

const TxStat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const TxStatValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.03em;
  font-variant-numeric: tabular-nums;
  line-height: 1;
`

const TxStatLabel = styled.div`
  color: #a1a1a1;
  font-size: 11px;
`
